import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

// True when running inside the Capacitor native shell (Android/iOS), where
// the @capacitor/camera plugin works. False in a desktop browser — those
// callers should fall back to the existing <input type="file"> flow.
export const isCameraNative = () => Capacitor.getPlatform() !== "web";

// Take ONE photo with the native camera. Skips the Camera/Photos prompt —
// callers expose a separate Gallery button for picking from the photo
// library so multi-select is possible there.
//
// Returns a File ready for the existing Firebase Storage uploadBytes flow,
// or null if the user cancelled.
export async function takePhotoNative() {
  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    resultType: CameraResultType.Base64,
    source: CameraSource.Camera,
  });
  if (!photo?.base64String) return null;
  const format = photo.format || "jpeg";
  const blob = base64ToBlob(photo.base64String, `image/${format}`);
  return new File([blob], `photo-${Date.now()}.${format}`, { type: blob.type });
}

// Take ONE photo OR pick ONE from the gallery — shows the native
// "Take Photo / Photo Library" prompt before opening either. For
// callers (like parts list) where one button + a chooser is friendlier
// than two separate Camera and Gallery buttons.
//
// Returns a File ready for upload, or null if cancelled.
export async function takePhotoPromptNative() {
  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    resultType: CameraResultType.Base64,
    source: CameraSource.Prompt,
    promptLabelHeader: "Add Photo",
    promptLabelPhoto: "Choose from Gallery",
    promptLabelPicture: "Take Photo",
  });
  if (!photo?.base64String) return null;
  const format = photo.format || "jpeg";
  const blob = base64ToBlob(photo.base64String, `image/${format}`);
  return new File([blob], `photo-${Date.now()}.${format}`, { type: blob.type });
}

// Pick multiple photos from the device gallery (up to `limit`). Returns
// an array of File objects, or [] if the user cancelled.
//
// pickImages returns webPath URLs rather than base64, so we fetch each
// one through the WebView (it serves blob: URLs) and wrap in a File.
export async function pickPhotosNative(limit = 10) {
  const result = await Camera.pickImages({ quality: 85, limit });
  const photos = result?.photos || [];
  const files = await Promise.all(
    photos.map(async (p, i) => {
      const response = await fetch(p.webPath);
      const blob = await response.blob();
      const format = p.format || "jpeg";
      return new File(
        [blob],
        `photo-${Date.now()}-${i}.${format}`,
        { type: blob.type || `image/${format}` }
      );
    })
  );
  return files;
}

function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: mimeType });
}
